import { QueryClient, useQuery } from '@tanstack/react-query';
import type { HelpTexts, Meta, SessionUser } from '../shared/types.ts';

export class ApiError extends Error {
  readonly status: number;
  readonly issues: Array<{ path: string; message: string }>;
  constructor(status: number, message: string, issues: Array<{ path: string; message: string }> = []) {
    super(message);
    this.status = status;
    this.issues = issues;
  }
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { 'X-Requested-With': 'dxa' };
  let payload: BodyInit | undefined;
  if (body instanceof FormData) payload = body;
  else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }
  const res = await fetch(`/api${url}`, { method, headers, body: payload, credentials: 'same-origin' });
  const data = res.headers.get('content-type')?.includes('application/json') ? await res.json() : null;
  if (!res.ok) throw new ApiError(res.status, data?.error ?? `Virhe ${res.status}`, data?.issues);
  return data as T;
}

export const api = {
  get: <T>(url: string) => request<T>('GET', url),
  post: <T>(url: string, body?: unknown) => request<T>('POST', url, body ?? {}),
  put: <T>(url: string, body?: unknown) => request<T>('PUT', url, body ?? {}),
  del: <T>(url: string) => request<T>('DELETE', url),
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
    },
  },
});

export const keys = {
  me: ['me'] as const,
  meta: ['meta'] as const,
  help: ['help'] as const,
  users: ['users'] as const,
  user: (id: string) => ['user', id.toUpperCase()] as const,
  fixed: (id: string) => ['user', id.toUpperCase(), 'fixed'] as const,
  qsos: (id: string, q: string) => ['user', id.toUpperCase(), 'qsos', q] as const,
  matrix: (id: string) => ['user', id.toUpperCase(), 'matrix'] as const,
  reports: ['reports'] as const,
  report: (id: number) => ['report', id] as const,
  competitions: ['competitions'] as const,
  competition: (id: number) => ['competition', id] as const,
};

export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: () => api.get<{ user: SessionUser | null }>('/auth/me').then((r) => r.user),
    staleTime: 5 * 60_000,
  });
}

export function useMeta() {
  return useQuery({ queryKey: keys.meta, queryFn: () => api.get<Meta>('/meta'), staleTime: 10 * 60_000 });
}

export function useHelpTexts() {
  return useQuery({ queryKey: keys.help, queryFn: () => api.get<HelpTexts>('/help'), staleTime: 10 * 60_000 });
}

/** Kun QSO:t tai kiinteät arvot muuttuvat, raportit ja käyttäjätiedot haetaan uudelleen */
export function invalidateData() {
  void queryClient.invalidateQueries({ queryKey: ['user'] });
  void queryClient.invalidateQueries({ queryKey: ['report'] });
  void queryClient.invalidateQueries({ queryKey: keys.users });
}

export function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Tuntematon virhe';
}
